import {
  filledByType,
  ImmutableChat,
  IScheduledVisits,
  ITemplate,
  ITemplateFormData,
} from "@home-health-notify/domain";
import { ValidationMode } from "@jsonforms/core";
import { JsonForms } from "@jsonforms/react";
import { Box, Button as LoadingButton } from "@mui/material";
import { ajv, useTranslation } from "hhn-commons";
import { useEffect, useMemo, useState } from "react";
import renderers, { customCellRenderers } from "./renderers";
import ScheduledVisits from "./ScheduledVisits";
import {
  EmptyFormErrorText,
  TemplateFormContainer,
} from "./TemplateForm.style";
import { ValidationModeJFC } from "@home-health-notify/templates";

interface TemplateFormProps {
  template: ITemplate;
  onSubmit: (data: ITemplateFormData) => void;
  isFormSubmitting: boolean;
  chat: ImmutableChat;
  selectedRole?: filledByType;
  formContainer?: React.ElementType;
}

const TemplateForm = ({
  template,
  onSubmit,
  isFormSubmitting,
  chat,
  selectedRole,
  formContainer,
}: TemplateFormProps) => {
  const {
    onHandleChange,
    onHandleChangeDependencies,
    schema,
    uiSchema,
    initialData,
    middleware,
  } = template;
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });
  const [formData, setFormData] = useState(initialData);
  const [scheduledVisits, setScheduledVisits] = useState<IScheduledVisits[]>(
    [],
  );
  const [conflictingVisitIds, setConflictingVisitIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<any[]>([]);

  const [validationMode, setValidationMode] = useState<ValidationMode>(
    ValidationModeJFC.ValidateAndHide,
  );

  const isFormEmpty = useMemo(
    () => (errors as any[])?.some(error => error.keyword === "anyOf"),
    [errors],
  );

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const handleSubmit = () => {
    if (errors?.length > 0) {
      setValidationMode(ValidationModeJFC.ValidateAndShow);
    } else {
      onSubmit && onSubmit(formData);
    }
  };
  // Perform validation only when both date and time are set
  useEffect(() => {
    const validateAndUpdateFormData = async () => {
      if (chat && onHandleChange) {
        const changeData: {
          formData?: ITemplateFormData;
          scheduledVisits?: IScheduledVisits[];
          conflictingVisitIds?: string[];
        } = await onHandleChange(formData, chat);
        if (changeData?.scheduledVisits)
          setScheduledVisits(changeData.scheduledVisits);
        setConflictingVisitIds(changeData?.conflictingVisitIds ?? []);
        if (changeData?.formData) setFormData(changeData.formData);
      }
    };

    validateAndUpdateFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...((onHandleChangeDependencies &&
      onHandleChangeDependencies?.(formData)) ||
      []),
    onHandleChange,
    chat,
  ]);

  const handleChange = async ({
    data,
    errors,
  }: {
    data: any;
    errors: any[];
  }) => {
    setFormData(data);
    setErrors(errors);
  };

  const StyledFormContainer: React.ElementType =
    formContainer ?? TemplateFormContainer;

  return (
    <StyledFormContainer>
      <JsonForms
        schema={schema}
        uischema={uiSchema}
        data={formData}
        renderers={renderers}
        cells={customCellRenderers}
        onChange={handleChange}
        validationMode={validationMode}
        ajv={ajv}
        config={{ chat, selectedRole }}
        middleware={middleware}
      />
      {isFormEmpty && validationMode === ValidationModeJFC.ValidateAndShow && (
        <EmptyFormErrorText>
          {tNs("templates.emptyFormError")}
        </EmptyFormErrorText>
      )}
      <Box>
        <LoadingButton
          loading={isFormSubmitting}
          variant="contained"
          onClick={handleSubmit}
          data-testid="template-submit-button"
        >
          {t("buttonLabels.submit")}
        </LoadingButton>
      </Box>
      {scheduledVisits.length > 0 && (
        <ScheduledVisits
          scheduledVisits={scheduledVisits}
          conflictingVisitIds={conflictingVisitIds}
        />
      )}
    </StyledFormContainer>
  );
};

export default TemplateForm;
